// CorridorDotsMapOptimised.jsx
import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import length from "@turf/length";
import along from "@turf/along";
import { Box, Typography, Slider, Stack } from "@mui/material";
import SoftBox from "components/SoftBox";

const bounds = {
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {},
      "geometry": {
        "coordinates": [
          [
            [
              151.06816073515188,
              -33.84679307728784
            ],
            [
              151.068354729134,
              -33.84717580172497
            ],
            [
              151.07078938987217,
              -33.84631498199853
            ],
            [
              151.070567443897,
              -33.84591882272915
            ],
            [
              151.07051732591924,
              -33.84593746462353
            ],
            [
              151.06816073515188,
              -33.84679307728784
            ]
          ]
        ],
        "type": "Polygon"
      }
    }
  ]
}


const MAPBOX_TOKEN = "pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw";
mapboxgl.accessToken = MAPBOX_TOKEN;

// ---------------- helpers ----------------
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function lerpCoord(c0, c1, t) {
  return [lerp(c0[0], c1[0], t), lerp(c0[1], c1[1], t)];
}
function normalizePct(x, fallback = 0.5) {
  if (x == null || Number.isNaN(Number(x))) return fallback;
  const v = Number(x);
  if (v > 1) return clamp(v / 100, 0, 1); // accept 0..100
  return clamp(v, 0, 1); // 0..1
}

function approxMetersBetween([lng1, lat1], [lng2, lat2]) {
  const mPerDegLat = 111320;
  const mPerDegLng = 111320 * Math.cos(((lat1 + lat2) * 0.5 * Math.PI) / 180);
  const dx = (lng2 - lng1) * mPerDegLng;
  const dy = (lat2 - lat1) * mPerDegLat;
  return Math.sqrt(dx * dx + dy * dy);
}

function sampleUCenterPower(k) {
  const sign = Math.random() < 0.5 ? -1 : 1;
  const r = Math.random();
  const x = Math.pow(r, 1 / Math.max(1e-6, k));
  const u = 0.5 + sign * 0.5 * x;
  return clamp(u, 0, 1);
}

function sampleCenterBiasedU(centerBias) {
  const uUniform = Math.random();
  if (centerBias <= 0) return uUniform;

  const r1 = Math.random();
  const r2 = Math.random();
  const tri = 0.5 + (r1 - r2) * 0.45;
  const uTri = clamp(tri, 0, 1);

  return lerp(uUniform, uTri, clamp(centerBias, 0, 1));
}

function sampleUWidthAware(corridor, s, centerBias) {
  const { L, R } = corridor.evalLR(s);
  const widthM = approxMetersBetween(L, R);

  const t = clamp((widthM - 5) / (18 - 5), 0, 1);
  const k = lerp(1.25, 4.5, t);

  const uWide = sampleUCenterPower(k);
  const uBase = sampleCenterBiasedU(centerBias);

  const mix = lerp(0.35, 0.8, t);
  return lerp(uBase, uWide, mix);
}

function sampleLine(lineFeature, sampleCount) {
  const km = length(lineFeature, { units: "kilometers" });
  const totalMeters = Math.max(1, km * 1000);
  const samples = [];
  for (let i = 0; i < sampleCount; i++) {
    const t01 = sampleCount === 1 ? 0 : i / (sampleCount - 1);
    const distKm = (t01 * totalMeters) / 1000;
    const pt = along(lineFeature, distKm, { units: "kilometers" });
    samples.push(pt.geometry.coordinates);
  }
  return { totalMeters, samples };
}

function normalizeSegment(a01, b01, minLen = 0.01) {
  let a = clamp(a01, 0, 1);
  let b = clamp(b01, 0, 1);
  if (b < a) [a, b] = [b, a];
  if (b - a < minLen) {
    const mid = (a + b) * 0.5;
    a = clamp(mid - minLen * 0.5, 0, 1);
    b = clamp(mid + minLen * 0.5, 0, 1);
  }
  return { a, b };
}

// Build polygon coords from sampled boundaries.
// Ring = leftSamples forward + rightSamples reversed (+ close)
function buildCorridorRing(leftSamples, rightSamples) {
  if (!leftSamples?.length || !rightSamples?.length) return null;
  const ring = [...leftSamples, ...[...rightSamples].reverse()];
  if (ring.length < 4) return null;

  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed =
    first && last && first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
  return closed;
}

function buildCorridorCache(corridors, sampleCount = 260) {
  return corridors.map((c) => {
    const L = sampleLine(c.leftBoundary, sampleCount);
    const R = sampleLine(c.rightBoundary, sampleCount);
    const corridorMeters = (L.totalMeters + R.totalMeters) / 2;

    const throughputA = Number(c.throughputA ?? 0) || 0;
    const throughputB = Number(c.throughputB ?? 0) || 0;

    const targetA = Math.max(0, Math.floor(Number(c.countA ?? 0) || 0));
    const targetB = Math.max(0, Math.floor(Number(c.countB ?? 0) || 0));

    const startPointA = normalizePct(c.startPointA, 0.0);
    const endPointA = normalizePct(c.endPointA, 1.0);
    const startPointB = normalizePct(c.startPointB, 0.0);
    const endPointB = normalizePct(c.endPointB, 1.0);

    const segA = normalizeSegment(startPointA, endPointA);
    const segB = normalizeSegment(startPointB, endPointB);

    // Corridor fill RGBA provided by you
    const fillRgba = c.fillRgba ?? c.fill ?? "rgba(79, 209, 197, 0.12)"; // default soft teal-ish
    const fillOpacity = Number.isFinite(c.fillOpacity) ? c.fillOpacity : 1;

    return {
      id: c.id,
      colorA: c.colorA ?? c.color ?? "#4FD1C5",
      colorB: c.colorB ?? "#63B3ED",

      fillRgba,
      fillOpacity,

      throughputA,
      throughputB,
      targetA,
      targetB,
      segA,
      segB,
      leftSamples: L.samples,
      rightSamples: R.samples,
      corridorMeters,
      sampleCount,
      evalLR(s01) {
        const s = clamp(s01, 0, 1);
        const x = s * (sampleCount - 1);
        const i0 = Math.floor(x);
        const i1 = Math.min(sampleCount - 1, i0 + 1);
        const t = x - i0;
        const L0 = this.leftSamples[i0];
        const L1 = this.leftSamples[i1];
        const R0 = this.rightSamples[i0];
        const R1 = this.rightSamples[i1];
        return { L: lerpCoord(L0, L1, t), R: lerpCoord(R0, R1, t) };
      },
      evalPoint(s01, u01) {
        const { L, R } = this.evalLR(s01);
        return lerpCoord(L, R, clamp(u01, 0, 1));
      },
      buildFillFeature() {
        const ring = buildCorridorRing(this.leftSamples, this.rightSamples);
        if (!ring) return null;
        return {
          type: "Feature",
          properties: {
            corridor: this.id,
            fill: this.fillRgba,
            fillOpacity: this.fillOpacity,
          },
          geometry: {
            type: "Polygon",
            coordinates: [ring],
          },
        };
      },
    };
  });
}

function getGeoJSONBounds(geojson) {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;

  const walk = (coords) => {
    if (!coords) return;
    if (typeof coords[0] === "number") {
      const [lng, lat] = coords;
      minLng = Math.min(minLng, lng);
      minLat = Math.min(minLat, lat);
      maxLng = Math.max(maxLng, lng);
      maxLat = Math.max(maxLat, lat);
      return;
    }
    coords.forEach(walk);
  };

  if (geojson?.type === "FeatureCollection") {
    geojson.features?.forEach((f) => walk(f.geometry?.coordinates));
  } else if (geojson?.type === "Feature") {
    walk(geojson.geometry?.coordinates);
  } else if (geojson?.coordinates) {
    walk(geojson.coordinates);
  }

  if (!isFinite(minLng)) return null;
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

// ---------------- DEMO ----------------
export const DEMO_CORRIDORS = [
  {
    id: "funnel-demo",
    colorA: "#4FD1C5",
    colorB: "#63B3ED",

    // NEW: fill colour between boundaries (RGBA string)
    fillRgba: "rgba(79, 209, 197, 0.10)",

    countA: 140,
    countB: 90,

    throughputA: 0.8,
    throughputB: 0.6,

    startPointA: 10,
    endPointA: 92,
    startPointB: 15,
    endPointB: 85,

    leftBoundary: {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [151.0704, -33.8461],
          [151.07005, -33.84635],
          [151.06975, -33.8466],
          [151.0695, -33.84685],
          [151.0693, -33.8471],
          [151.0691, -33.84735],
        ],
      },
    },
    rightBoundary: {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [151.07105, -33.84625],
          [151.07055, -33.84648],
          [151.0701, -33.8467],
          [151.06972, -33.84692],
          [151.06965, -33.84715],
          [151.06975, -33.84745],
        ],
      },
    },
  },
];

// ---------------- Component ----------------
export default function CorridorDotsMapOptimised({
  corridors = DEMO_CORRIDORS,
  initialCenter = [151.06953278898038, -33.84648698566861],//-33.84648698566861, 151.06953278898038

  prefill = true,
  animate = true,
  corridorChangeDelayMs = 500,

  initialDotSize = 3.8,
  initialDotBlur = 0.35,
  children
}) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const rafRef = useRef(null);
  const lastTRef = useRef(null);

  // readiness + queued updates
  const mapReadyRef = useRef(false);
  const pendingApplyRef = useRef(false);

  // runtime refs
  const corridorsPropRef = useRef(corridors);
  useEffect(() => void (corridorsPropRef.current = corridors), [corridors]);

  const animateRef = useRef(animate);
  useEffect(() => void (animateRef.current = animate), [animate]);

  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);

  const cacheRef = useRef([]);
  const idRef = useRef(1);

  const objectsRef = useRef([]);
  const accRef = useRef({ a: 0, b: 0 });

  const [dotSize, setDotSize] = useState(initialDotSize);
  const [dotBlur, setDotBlur] = useState(initialDotBlur);
  const [centerBias, setCenterBias] = useState(0.65);
  const [crossDrift, setCrossDrift] = useState(0.02);

  const [speedA, setSpeedA] = useState(3.6);
  const [speedB, setSpeedB] = useState(2.8);

  const buildFeatureCollection = () => {
    const cache = cacheRef.current;
    const features = [];

    for (const o of objectsRef.current) {
      const corridor = cache[o.corridorIdx];
      if (!corridor) continue;

      const coord = corridor.evalPoint(o.s, o.u);

      const seg = o.dir === 1 ? corridor.segA : corridor.segB;
      const t01 = (o.s - seg.a) / Math.max(1e-6, seg.b - seg.a);
      const edgeFade = clamp(Math.min(t01, 1 - t01) * 8, 0, 1);

      features.push({
        type: "Feature",
        properties: {
          color: o.dir === 1 ? corridor.colorA : corridor.colorB,
          r: dotSize,
          a: edgeFade,
          b: dotBlur,
        },
        geometry: { type: "Point", coordinates: coord },
      });
    }

    return { type: "FeatureCollection", features };
  };

  const renderNow = () => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) return;
    const src = map.getSource("flow-dots");
    if (src) src.setData(buildFeatureCollection());
  };

  const removeOne = (dir, corridorIdx) => {
    const idx = objectsRef.current.findIndex((o) => o.dir === dir && o.corridorIdx === corridorIdx);
    if (idx >= 0) objectsRef.current.splice(idx, 1);
  };

  const doPrefill = () => {
    const cache = cacheRef.current;
    if (!cache.length) {
      objectsRef.current = [];
      return;
    }

    const objs = [];
    const addObj = (dir, corridorIdx) => {
      const corridor = cache[corridorIdx];
      const seg = dir === 1 ? corridor.segA : corridor.segB;

      const s = lerp(seg.a, seg.b, Math.random());
      const u = sampleUWidthAware(corridor, s, centerBias);

      const drift = (Math.random() * 2 - 1) * 0.16;
      const speedMult = 0.7 + Math.random() * 0.95;

      objs.push({
        id: idRef.current++,
        corridorIdx,
        s,
        u,
        dir,
        speedMult,
        drift,
      });
    };

    for (let i = 0; i < cache.length; i++) {
      for (let k = 0; k < cache[i].targetA; k++) addObj(1, i);
      for (let k = 0; k < cache[i].targetB; k++) addObj(-1, i);
    }

    objectsRef.current = objs;
    accRef.current = { a: 0, b: 0 };
  };

  const applyCorridorsUpdate = () => {
    // Always rebuild cache + objects, even if animation is off.
    // If map isn't ready yet, queue a later apply.
    cacheRef.current = buildCorridorCache(corridorsPropRef.current, 240);

    if (prefill) doPrefill();

    const map = mapRef.current;
    if (!map || !mapReadyRef.current) {
      pendingApplyRef.current = true;
      return;
    }

    // 1) boundaries update
    const boundarySrc = map.getSource("corridor-boundaries");
    if (boundarySrc) {
      const boundaryFC = {
        type: "FeatureCollection",
        features: corridorsPropRef.current.flatMap((c) => [
          {
            ...c.leftBoundary,
            properties: {
              ...(c.leftBoundary.properties || {}),
              corridor: c.id,
              side: "left",
            },
          },
          {
            ...c.rightBoundary,
            properties: {
              ...(c.rightBoundary.properties || {}),
              corridor: c.id,
              side: "right",
            },
          },
        ]),
      };
      boundarySrc.setData(boundaryFC);
    }

    // 2) NEW: corridor fill polygon update
    const fillSrc = map.getSource("corridor-fill");
    if (fillSrc) {
      const fillFeatures = cacheRef.current
        .map((cc) => cc.buildFillFeature())
        .filter(Boolean);

      fillSrc.setData({
        type: "FeatureCollection",
        features: fillFeatures,
      });
    }

    // render immediately (THIS is the key for animate=false)
    renderNow();

    // only pause/resume matters when animate is true
    pausedRef.current = true;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, corridorChangeDelayMs);
  };

  // ---- Create map ONCE ----
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: initialCenter,
      zoom: 16,
      bearing: 0,
      antialias: true,
      attributionControl: false,
      logoPosition: 'bottom-right'
    });

    map.addControl(new mapboxgl.AttributionControl({
      compact: true
    }), 'bottom-right');

    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", () => {
      mapReadyRef.current = true;

      // NEW: corridor fill source
      map.addSource("corridor-fill", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addSource("corridor-boundaries", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addSource("flow-dots", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      // NEW: fill layer (below everything)
      map.addLayer({
        id: "corridor-fill-layer",
        type: "fill",
        source: "corridor-fill",
        paint: {
          "fill-color": ["coalesce", ["get", "fill"], "rgba(0,0,0,0.08)"],
          "fill-opacity": ["coalesce", ["get", "fillOpacity"], 1],
        },
      });

      // boundaries layer
      map.addLayer({
        id: "corridor-boundaries-layer",
        type: "line",
        source: "corridor-boundaries",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#A0AEC0",
          "line-opacity": 0.18,
          "line-width": 3,
        },
      });

      // dots glow + core layers
      map.addLayer({
        id: "flow-dots-glow",
        type: "circle",
        source: "flow-dots",
        paint: {
          "circle-color": ["get", "color"],
          "circle-radius": ["*", ["get", "r"], 1.7],
          "circle-opacity": ["*", ["get", "a"], 0.35],
          "circle-blur": 0.9,
        },
      });

      map.addLayer({
        id: "flow-dots-core",
        type: "circle",
        source: "flow-dots",
        paint: {
          "circle-color": ["get", "color"],
          "circle-radius": ["get", "r"],
          "circle-opacity": ["get", "a"],
          "circle-blur": ["get", "b"],
        },
      });


      mapRef.current.fitBounds(getGeoJSONBounds(bounds), {
        padding: 60,
        duration: 800,
        bearing: 0
      });


      // Apply any queued corridor updates
      applyCorridorsUpdate();
      if (pendingApplyRef.current) {
        pendingApplyRef.current = false;
        applyCorridorsUpdate();
      }

      const tick = (ts) => {
        if (!lastTRef.current) lastTRef.current = ts;
        const dt = (ts - lastTRef.current) / 1000;
        lastTRef.current = ts;

        // If animation off, keep RAF alive but do nothing expensive.
        if (!animateRef.current || pausedRef.current) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }

        const cache = cacheRef.current;
        if (!cache.length) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }

        // current counts
        const countA = new Array(cache.length).fill(0);
        const countB = new Array(cache.length).fill(0);
        for (const o of objectsRef.current) {
          if (o.dir === 1) countA[o.corridorIdx]++;
          else countB[o.corridorIdx]++;
        }

        const acc = accRef.current;

        const spawnOne = (dir, corridorIdx) => {
          const corridor = cache[corridorIdx];
          const seg = dir === 1 ? corridor.segA : corridor.segB;

          const eps = 0.03;
          const band = Math.min(eps, (seg.b - seg.a) * 0.35);

          const s =
            dir === 1
              ? lerp(seg.a, seg.a + band, Math.random())
              : lerp(seg.b - band, seg.b, Math.random());

          const u = sampleUWidthAware(corridor, s, centerBias);

          const drift = (Math.random() * 2 - 1) * 0.16;
          const speedMult = 0.7 + Math.random() * 0.95;

          objectsRef.current.push({
            id: idRef.current++,
            corridorIdx,
            s,
            u,
            dir,
            speedMult,
            drift,
          });
        };

        // population adjust per corridor
        for (let i = 0; i < cache.length; i++) {
          const c = cache[i];

          const deltaA = c.targetA - countA[i];
          if (deltaA !== 0) acc.a += Math.sign(deltaA) * c.throughputA * dt;

          while (acc.a >= 1 && deltaA > 0) {
            spawnOne(1, i);
            acc.a -= 1;
            countA[i]++;
          }
          while (acc.a <= -1 && deltaA < 0) {
            removeOne(1, i);
            acc.a += 1;
            countA[i]--;
          }

          const deltaB = c.targetB - countB[i];
          if (deltaB !== 0) acc.b += Math.sign(deltaB) * c.throughputB * dt;

          while (acc.b >= 1 && deltaB > 0) {
            spawnOne(-1, i);
            acc.b -= 1;
            countB[i]++;
          }
          while (acc.b <= -1 && deltaB < 0) {
            removeOne(-1, i);
            acc.b += 1;
            countB[i]--;
          }
        }

        // advance
        const next = [];
        for (const o of objectsRef.current) {
          const corridor = cache[o.corridorIdx];
          if (!corridor) continue;

          const seg = o.dir === 1 ? corridor.segA : corridor.segB;

          const metersPerSec = (o.dir === 1 ? speedA : speedB) * o.speedMult;
          const ds = (metersPerSec * dt) / corridor.corridorMeters;
          o.s += o.dir * ds;

          o.u += o.drift * crossDrift * dt * 4.0;
          if (o.u < 0.06) o.drift += 0.22 * dt;
          if (o.u > 0.94) o.drift -= 0.22 * dt;
          o.u = clamp(o.u, 0, 1);

          if (o.dir === 1) {
            if (o.s > seg.b) continue;
            if (o.s < seg.a) o.s = seg.a;
          } else {
            if (o.s < seg.a) continue;
            if (o.s > seg.b) o.s = seg.b;
          }

          next.push(o);
        }
        objectsRef.current = next;

        renderNow();
        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    });

    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (mapRef.current) mapRef.current.remove();
      mapRef.current = null;
      mapReadyRef.current = false;
      lastTRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // corridor changes should ALWAYS apply + render (even when animate=false)
  useEffect(() => {
    applyCorridorsUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corridors]);

  // If animation is turned off, force a render so the user sees current state.
  useEffect(() => {
    animateRef.current = animate;
    if (!animate) renderNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate]);

  // look changes should immediately render too
  useEffect(() => {
    renderNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotSize, dotBlur]);

  useEffect(() => {
    applyCorridorsUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // ---------------- Soft UI panel (optional) ----------------
  // (You currently return only the map; leaving the panel code minimal and unobtrusive.)
  const labelSx = { fontSize: 12.5, opacity: 0.8 };
  const valueSx = { fontWeight: 800 };

  const LabeledSlider = ({ label, value, fmt, min, max, step, onChange }) => (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={labelSx}>{label}</Typography>
        <Typography sx={valueSx}>{fmt ? fmt(value) : value}</Typography>
      </Stack>
      <Slider value={value} min={min} max={max} step={step} onChange={(_, v) => onChange(v)} />
    </Box>
  );

  // If you want the controls overlay back, wrap map in a container and absolutely position a panel.
  // For now, keeping your exact return style:
  return <SoftBox>
    <SoftBox ref={mapContainerRef} sx={{ height: "100%", width: "100%", position: "absolute" }} />
    <SoftBox sx={{ height: "100%", width: "100%", position: "absolute" }} >
      {children}
    </SoftBox>
  </SoftBox>

}
